"use client";

import { ReactNode, useEffect } from "react";
import { centralizedLogout, checkSession } from "./services/authService";
import { userVar } from "./utils/reactive-variable";
import { useIsBrowser } from "@/hooks/browser-only";
import { handleLoginUpdate } from "./utils/loginHelpers";
import {
  shouldCheckSession,
  validateAndUpdateUserFromToken,
} from "./utils/tokenHelpers";
import { AuthMode } from "@/generated/graphql";
import { CookieEnum, eraseCookie } from "@/shared/utils/cookie";

interface AuthProviderProps {
  children: ReactNode;
  storeId?: string | null;
  storeMode?: AuthMode;
  isWuiltPay?: boolean;
}

export function AuthProvider({
  children,
  storeId,
  storeMode,
  isWuiltPay,
}: AuthProviderProps) {
  const isBrowser = useIsBrowser();

  useEffect(() => {
    if (!isBrowser || !storeId) return;

    (async () => {
      try {
        // Check if token exists and validate/update user context
        if (!shouldCheckSession()) {
          // Token exists, validate it and update user context
          validateAndUpdateUserFromToken();
          return;
        }

        // No token found, proceed with checkSession
        const response = await checkSession(storeId!);
        if (response?.success && response?.data) {
          // Create an updateUser function that properly merges user data
          const updateUser = (userData: any) => {
            const currentUser = userVar();
            const updatedUser = {
              ...currentUser,
              ...userData,
            };
            userVar(updatedUser);
          };

          handleLoginUpdate({
            loginResponseData: response.data,
            token: response?.token,
            updateUser,
          });
        }
      } catch {
        if (storeMode === AuthMode.Modern && isWuiltPay) {
          centralizedLogout();
          eraseCookie(CookieEnum.AUTH_TOKEN, { sameSite: "strict" });
          eraseCookie(CookieEnum.CLIENT_CENTRAL_TOKEN, { sameSite: "strict" });
          localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
          userVar(undefined);
        }
        return;
      }
    })();
  }, [isBrowser, isWuiltPay, storeId, storeMode]);

  return <>{children}</>;
}
